"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import CtaButton from "@/components/ui/buttons/CtaButton";
import type { NewsItem } from "@/types";

// How long each item stays visible (including the crossfade) and how long the
// fade out/in takes. Pure CSS opacity transition — no animation library needed.
const ROTATE_MS = 6000;
const FADE_MS = 500;

// Shown behind items that have neither a tour image nor a custom upload.
const FALLBACK_IMAGE = "/assets/image-header-inverse-2.webp";

// Label used when the button is enabled but no custom text was entered.
const DEFAULT_CTA_TEXT = "Pozrieť voľné termíny";

export default function NewsBanner({ news }: { news?: NewsItem[] }) {
  // Real items from Kirby; fall back to the sample only in development so the
  // banner stays visible while the CMS is still empty.
  const items = news ?? []

  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  // Auto-rotate: fade the active promo out, swap to the next item, fade back in.
  // Uses a functional state update so the interval never reads a stale index.
  useEffect(() => {
    if (items.length <= 1) return;

    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % items.length);
        setVisible(true);
      }, FADE_MS);
    }, ROTATE_MS);

    return () => clearInterval(id);
  }, [items.length]);

  // Flexible title size: shrink the font so the title fits on one line within
  // the available width. Falls back to wrapping only if it would get too small.
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const MIN = 15; // px floor before we allow the title to wrap
    const MD = 768; // Tailwind `md` breakpoint
    const fit = () => {
      const el = titleRef.current;
      if (!el) return;
      el.style.fontSize = ""; // reset to the responsive CSS base size

      // On mobile, let the title wrap to multiple lines at its base size
      // instead of shrinking it to fit on one line.
      if (window.innerWidth < MD) {
        el.style.whiteSpace = "normal";
        return;
      }

      el.style.whiteSpace = "nowrap";
      const base = parseFloat(getComputedStyle(el).fontSize);
      const available = el.clientWidth;
      const needed = el.scrollWidth;
      if (available > 0 && needed > available) {
        const ideal = (base * available) / needed;
        if (ideal >= MIN) {
          el.style.fontSize = `${ideal}px`;
        } else {
          el.style.fontSize = `${MIN}px`;
          el.style.whiteSpace = "normal";
        }
      }
    };

    fit();
    window.addEventListener("resize", fit);
    // Re-fit once the Kalam font loads — it changes the measured text width.
    document.fonts?.ready?.then(fit);

    return () => window.removeEventListener("resize", fit);
  }, [index, items]);

  const select = (i: number) => {
    if (i === index) return;
    setVisible(false);
    setTimeout(() => {
      setIndex(i);
      setVisible(true);
    }, FADE_MS);
  };

  if (!items.length) return null;

  const active = items[index] ?? items[0];

  return (
    <section className="max-w-300 mx-auto px-6 md:px-0 mb-4 md:mb-2 text-left">
      <div className="relative flex flex-col overflow-hidden rounded-2xl text-white shadow-lg md:flex-row">
        {/* Full-bleed background image — crossfades between items with a slow zoom */}
        <div className="absolute inset-0 bg-[#00638A]">
          {items.map((item, i) => (
            <div
              key={item.id}
              className="absolute inset-0 transition-opacity duration-700 ease-out"
              style={{ opacity: i === index ? 1 : 0 }}
            >
              <Image
                src={item.image ?? FALLBACK_IMAGE}
                alt=""
                fill
                unoptimized
                priority={i === 0}
                sizes="100vw"
                className="animate-kenburns object-cover"
              />
            </div>
          ))}
          {/* Gradient overlay keeps the text readable over any photo */}
          <div className="absolute inset-0 bg-linear-to-r from-[#00638A]/95 via-[#00638A]/75 to-[#00638A]/40 md:to-transparent" />
        </div>

        {/* Active promo — crossfades on rotation; green accent strip along the bottom */}
        <div
          className="relative z-10 gap-y-2 flex flex-1 flex-col items-start p-6 transition-opacity ease-out md:justify-center md:px-8 md:py-4"
          style={{
            opacity: visible ? 1 : 0,
            transitionDuration: `${FADE_MS}ms`,
          }}
        >
          {active.tag && (
            <span
              className="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide"
              style={{ backgroundColor: active.accent }}
            >
              {active.tag}
            </span>
          )}

          <h3
            ref={titleRef}
            className="w-full max-w-2xl mt-2 overflow-hidden font-kalam font-bold text-2xl leading-[1.15] drop-shadow-md md:text-[1.7rem]"
          >
            {active.title}
          </h3>

          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-white/90 drop-shadow md:flex md:flex-wrap md:gap-x-5 md:gap-y-1">
            {active.duration && <Meta icon="clock" label={active.duration} />}
            {active.departure && <Meta icon="pin" label={active.departure} />}
            {active.date && <Meta icon="calendar" label={active.date} />}
            {active.secureText && <Meta icon="shield" label={active.secureText} />}
          </div>

          {active.showCta && (
            <div className="mt-1">
              <CtaButton
                text={active.ctaText ?? DEFAULT_CTA_TEXT}
                href={active.ctaHref}
                arrow
              />
            </div>
          )}
        </div>

        {/* News list — a row on mobile, a vertical panel on the right on desktop.
            Active item: lighter box + bold title everywhere, plus a green left
            bar on desktop. */}
        <div className="relative z-10 flex flex-row justify-center gap-2 bg-[#013647] px-0 py-0 md:w-56 md:flex-col md:justify-between md:gap-1 md:px-0 md:py-0">
          {items.map((item, i) => {
            const isActive = i === index;
            return (
              <button
                key={item.id}
                onClick={() => select(i)}
                style={{
                  borderTopColor: isActive ? item.accent : "transparent",
                  borderLeftColor: isActive ? item.accent : "transparent",
                }}
                className={`flex flex-1 flex-col md:items-start items-center justify-center cursor-pointer text-center px-2 py-3.5 border-t-[3px] transition-colors duration-200 md:block md:flex-none md:text-left md:border-t-0 md:border-l-[3px] md:px-4 md:py-0 md:pb-3 md:pt-2 ${
                  isActive ? "bg-white/10" : "hover:bg-white/5"
                }`}
              >
                {item.tag && (
                  <span
                    className="inline-block rounded px-2 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide"
                    style={{
                      backgroundColor: `${item.accent}33`,
                      color: item.accent,
                    }}
                  >
                    {item.tag}
                  </span>
                )}
                <span
                  className={`mt-1 block text-xs md:text-sm ${
                    isActive ? "text-white" : "text-white/90"
                  }`}
                >
                  {item.listTitle}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Meta({
  icon,
  label,
}: {
  icon: "clock" | "pin" | "calendar" | "shield";
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <MetaIcon icon={icon} />
      {label}
    </span>
  );
}

function MetaIcon({
  icon,
}: {
  icon: "clock" | "pin" | "calendar" | "shield";
}) {
  const common = {
    width: 15,
    height: 15,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className: "opacity-80",
  };

  if (icon === "clock") {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    );
  }

  if (icon === "pin") {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" {...common}>
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    );
  }

  if (icon === "shield") {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" {...common}>
        <path d="M12 3 4 6v6c0 4.5 3.2 7.6 8 9 4.8-1.4 8-4.5 8-9V6l-8-3z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
  }

  return (
    <svg xmlns="http://www.w3.org/2000/svg" {...common}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

